import type { DataEntryComponent, DataEntryStoredData } from '../../../../api/pages-client/pages-client.type';
import { DataEntryDataType } from '../../../../api/pages-client/pages-client.type';

/**
 * Initializes row data for a data entry component with default values based on column data types.
 *
 * @param {DataEntryComponent} component - The data entry component containing column definitions
 * @returns {Record<string, string | number | boolean | null>} A record object with column names as keys and default values based on their data types:
 *   - TEXT: empty string ('')
 *   - INTEGER/FLOAT: zero (0)
 *   - BOOLEAN: false
 *   - DATE/DATETIME: null
 *   - default: null
 */

function initializeRowData(component: DataEntryComponent): Record<string, string | number | boolean | null> {
  const initialData: Record<string, string | number | boolean | null> = {};
  component.column_data?.forEach((column) => {
    switch (column.data_type) {
      case DataEntryDataType.TEXT:
        initialData[column.name] = '';
        break;
      case DataEntryDataType.INTEGER:
      case DataEntryDataType.FLOAT:
        initialData[column.name] = 0;
        break;
      case DataEntryDataType.BOOLEAN:
        initialData[column.name] = false;
        break;
      case DataEntryDataType.DATE:
      case DataEntryDataType.DATETIME:
        initialData[column.name] = null;
        break;
      default:
        initialData[column.name] = null;
    }
  });
  return initialData;
}

/**
 * Validates row data for a data entry component to ensure all required fields are filled.
 *
 * @param {DataEntryStoredData} rowData - The row data to validate, containing column names as keys and their values
 * @param {DataEntryComponent} component - The data entry component containing column definitions and validation rules
 * @returns {{ isValid: boolean; errorMessage: string }} An object containing:
 *   - isValid: true if all required fields are filled, false otherwise
 *   - errorMessage: empty string if valid, otherwise a descriptive error message listing missing fields
 *
 * @description
 * Validation rules:
 * - TEXT fields: allow empty strings but require non-null/undefined values
 * - All other data types (INTEGER, FLOAT, BOOLEAN, DATE, DATETIME): require non-empty values (not null, undefined, or empty string)
 * - Returns a user-friendly error message listing all missing required fields
 */

function validateRowData(
  rowData: DataEntryStoredData,
  component: DataEntryComponent,
): { isValid: boolean; errorMessage: string } {
  const emptyFields: string[] = [];
  const isValid = component.column_data?.every((column) => {
    const value = rowData[column.name];

    // For text fields, allow empty strings
    if (column.data_type === DataEntryDataType.TEXT) {
      return value !== null && value !== undefined;
    }

    // For other data types, require non-empty values
    const isEmpty = value === null || value === undefined || value === '';
    if (isEmpty) {
      emptyFields.push(column.name);
    }
    return !isEmpty;
  });

  return {
    isValid: isValid ?? false,
    errorMessage: isValid ? '' : `Please fill in all required fields: ${emptyFields.join(', ')}`,
  };
}

export { initializeRowData, validateRowData };
